/**
 * Sanitizes embed content to prevent XSS attacks while preserving legitimate scripts and styles
 * Custom implementation that only strips dangerous event handlers
 * @param html - The raw HTML content to sanitize
 * @returns The sanitized HTML content
 */
export const sanitizeEmbed = (html: string): string => {
	if (!html || typeof html !== 'string') {
		return '';
	}

	try {
		const dangerousHandlers = [
			'onerror',
			'onload',
			'onbeforeunload',
			'onunload',
			'onmessage',
		];

		// Handle malformed attributes like src/onerror or href/onclick
		const malformedAttributePattern = new RegExp(
			`\\s+(src|href|action|data|poster|srcset|formaction)\\s*/\\s*(${dangerousHandlers.join('|')})\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*)`,
			'gi',
		);

		let sanitized = html.replace(/<[^>]+>/g, (tag) => tag.replace(malformedAttributePattern, ' $1=""'));

		// Handle whitespace-separated dangerous handlers
		const dangerousHandlerPattern = new RegExp(
			`\\s+(${dangerousHandlers.join('|')})\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*)`,
			'gi',
		);

		sanitized = sanitized.replace(/<[^>]+>/g, (tag) => tag.replace(dangerousHandlerPattern, ''));

		// Handle standalone slash-separated dangerous handlers (fixes new vulnerability)
		const slashSeparatedHandlerPattern = new RegExp(
			`/(${dangerousHandlers.join('|')})\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*)`,
			'gi',
		);

		sanitized = sanitized.replace(/<[^>]+>/g, (tag) => tag.replace(slashSeparatedHandlerPattern, ''));

		sanitized = sanitized.replace(
			/\s+(href|src)\s*=\s*["']?\s*javascript:/gi,
			' $1=""',
		);

		// Clean up trailing spaces before closing bracket in tags
		sanitized = sanitized.replace(/<([^>]+)\s+>/g, '<$1>');

		return sanitized;
	} catch (error) {
		console.error('Custom sanitization error:', error);

		return '';
	}
};
